import { useEffect } from 'react'
import { APP_DESCRIPTION, APP_KEYWORDS, APP_NAME, APP_ROBOTS } from '@/config'

interface SeoProps {
  title: string
  description?: string
  keywords?: string[]
  path?: string
  structuredData?: Array<Record<string, unknown>>
}

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

function upsertLink(rel: string, href: string) {
  let element = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', rel)
    document.head.appendChild(element)
  }
  element.setAttribute('href', href)
}

export function Seo({
  title,
  description = APP_DESCRIPTION,
  keywords = APP_KEYWORDS,
  path,
  structuredData = [],
}: SeoProps) {
  const keywordContent = keywords.join(', ')
  const structuredDataKey = JSON.stringify(structuredData)

  useEffect(() => {
    const origin = window.location.origin
    const canonicalUrl = new URL(path || window.location.pathname, origin).toString()

    document.title = title

    upsertMeta('name', 'description', description)
    upsertMeta('name', 'keywords', keywordContent)
    upsertMeta('name', 'robots', APP_ROBOTS)
    upsertMeta('name', 'author', APP_NAME)
    upsertMeta('name', 'application-name', APP_NAME)
    upsertMeta('name', 'apple-mobile-web-app-title', APP_NAME)
    upsertMeta('property', 'og:type', 'website')
    upsertMeta('property', 'og:site_name', APP_NAME)
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:url', canonicalUrl)
    upsertMeta('name', 'twitter:card', 'summary')
    upsertMeta('name', 'twitter:title', title)
    upsertMeta('name', 'twitter:description', description)
    upsertLink('canonical', canonicalUrl)

    const graph = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          name: APP_NAME,
          url: origin,
          description: APP_DESCRIPTION,
        },
        {
          '@type': 'WebSite',
          name: APP_NAME,
          url: origin,
          description: APP_DESCRIPTION,
          inLanguage: 'en',
        },
        {
          '@type': 'SoftwareApplication',
          name: APP_NAME,
          applicationCategory: 'FinanceApplication',
          operatingSystem: 'Web',
          isAccessibleForFree: true,
          url: origin,
          description: APP_DESCRIPTION,
        },
        {
          '@type': 'WebPage',
          name: title,
          url: canonicalUrl,
          description,
          inLanguage: 'en',
        },
        ...structuredData,
      ],
    }

    let script = document.head.querySelector('#app-seo-ld-json') as HTMLScriptElement | null
    if (!script) {
      script = document.createElement('script')
      script.id = 'app-seo-ld-json'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(graph)
  }, [title, description, keywordContent, path, structuredDataKey])

  return null
}